import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import LoadingButton from '@mui/lab/LoadingButton';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
// routes
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
// components
import Iconify from 'src/components/iconify';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import {
  updateUserDetailsApi,
  updateUserPassword,
} from 'src/services/user.service';
import { Button, Card, Typography } from '@mui/material';
import { paths } from 'src/routes/paths';
import { useSnackbar } from 'src/components/snackbar';
import { useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { usePathname, useRouter } from 'src/routes/hooks';
import { LoadingScreen } from 'src/components/loading-screen';
import { useResponsive } from 'src/hooks/use-responsive';
import { useForm } from 'react-hook-form';

// ----------------------------------------------------------------------

export default function ChangePassword() {
  // hooks
  const { enqueueSnackbar } = useSnackbar();
  const router = useRouter();
  const password = useBoolean();
  const [searchParams] = useSearchParams();
  const mdUp = useResponsive('up', 'md');
  const pathname = usePathname();

  // states
  const [isLoading, setIsLoading] = useState(true);

  // variables
  const token = searchParams.get('token');
  const isPublic = !pathname.includes('/dashboard/change-password');

  const verifySchema = Yup.object().shape({
    password: Yup.string().required('Password is required'),
    confirmPassword: Yup.string()
      .required('Please confirm password')
      .oneOf([Yup.ref('password')], 'Passwords must match'),
  });

  const defaultValues = {
    password: '',
    confirmPassword: '',
  };

  const methods = useForm({
    mode: 'onBlur',
    resolver: yupResolver(verifySchema),
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  // functions
  const onSubmit = handleSubmit(async data => {
    try {
      const payload = {
        password: data.confirmPassword,
      };

      let response = null;

      if (token) {
        response = await updateUserPassword(payload, token);
      } else {
        response = await updateUserDetailsApi(payload);
      }

      if (response.success) {
        enqueueSnackbar('Password Changed Successfully');
        router.push(!isPublic ? paths.dashboard.root : paths.auth.jwt.login);
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Something went wrong', { variant: 'error' });
    }
  });

  // effects
  useEffect(() => {
    if (!token && isPublic) {
      router.push(paths.page401);
    }

    setIsLoading(false);
  }, [isPublic, router, token]);

  return isLoading ? (
    <LoadingScreen />
  ) : (
    <Stack
      component={mdUp ? Card : 'div'}
      sx={{
        p: {
          xs: '10px',
          md: '16px',
        },
        minHeight: { xs: 'calc(100vh - 64px)', md: '0px' },
        width: { xs: '100%', md: '60%' },
        marginTop: { xs: '80px', md: '100px' },
        margin: { md: 'auto' },
      }}
    >
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <Stack spacing={3} alignItems={{ xs: 'flex-start', md: 'center' }}>
          <Typography variant="h4">Change Password</Typography>
          <RHFTextField
            name="password"
            placeholder="Enter Password"
            type={password.value ? 'text' : 'password'}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={password.onToggle} edge="end">
                    <Iconify
                      icon={
                        password.value
                          ? 'solar:eye-bold'
                          : 'solar:eye-closed-bold'
                      }
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <RHFTextField
            name="confirmPassword"
            placeholder="Confirm New Password"
            type={password.value ? 'text' : 'password'}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={password.onToggle} edge="end">
                    <Iconify
                      icon={
                        password.value
                          ? 'solar:eye-bold'
                          : 'solar:eye-closed-bold'
                      }
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Stack
            sx={{
              position: {
                xs: 'absolute',
                md: 'static',
              },
              bottom: 0,
              left: 0,
              right: 0,
              width: '100%',
              padding: {
                xs: '16px 16px 28px 16px',
                md: '0px',
              },
              flexDirection: {
                xs: 'column-reverse',
                md: 'column',
              },
              gap: '8px',
            }}
          >
            <LoadingButton
              fullWidth
              type="submit"
              variant="contained"
              loading={isSubmitting}
            >
              Update Password
            </LoadingButton>
            <Button
              fullWidth
              startIcon={<Iconify icon="tabler:arrow-left" />}
              href={isPublic ? paths.auth.jwt.login : paths.dashboard.root}
            >
              <Typography variant="input" fontWeight={600}>
                {isPublic ? 'Return to signin' : 'Return to dashboard'}
              </Typography>
            </Button>
          </Stack>
        </Stack>
      </FormProvider>
    </Stack>
  );
}
